import { useEffect, useRef } from "react"
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react"
import _ from "lodash-es"

import Button from "react-bootstrap/esm/Button.js"
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationTaskModal from "../../components/ConfirmationTaskModal"
import DebugAccordion from "../../components/DebugAccordion"
import Icon from "../../components/Icon"
import PageTitle from "../../components/PageTitle.jsx"
import TimestampWithUTCTooltip from "../../components/TimestampWithUTCTooltip"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx";
import VersionTooltip from "../../components/VersionTooltip.jsx"
import { addNotification, NotificationTypes } from "../../stores/notification-store"
import { deserializeJsonDotNetDate } from '../../utils/utils.js'

import useAgentManifest from "../../hooks/useAgentManifest.js"
import useComponentConfigData, { componentConfigData } from "../../hooks/useComponentConfigData.js"
import useMachineStatusData, { machineStatusData } from "../../hooks/useMachineStatusData.js"
import useUtgData from "../../hooks/useUtgData";
import useUtgManifest from "../../hooks/useUtgManifest.js"
import useComponentData from "../../hooks/useComponentData.js"
import useUtgTaskList from "../../hooks/useUtgTaskList.js"
import useUtgDeviceList from "../../hooks/useUtgDeviceList.js"
import { UpdateComponent, DeleteTask } from "../../utils/api.js"

export default function UtgDetail() {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { beId, componentId } = useParams()
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()

    const { utg, loading: utgLoading, error: utgError } = useUtgData(beId, componentId, config, accessToken)
    const { component, loading: componentLoading, error: componentError } = useComponentData(beId, componentId, config, accessToken)
    const utgConnectionData = useComputed(() => {
        // this is an embedded json string within the utg json. Extract it so we can display
        if (utg.value.ComponentConnectionString) {
            return JSON.parse(utg.value.ComponentConnectionString)
        }
        return ''
    })
    const { loading: componentConfigLoading } = useComponentConfigData(beId, utg.value, config, accessToken)
    const { loading: machineStatusLoading } = useMachineStatusData(beId, utg.value, config, accessToken)
    const { tasks, loading: tasksLoading, modified: taskListModified, fetchData: reFetchTaskList } = useUtgTaskList(beId, componentId, config, accessToken)
    const { devices, loading: devicesLoading } = useUtgDeviceList(beId, componentId, config, accessToken)

    const { agents: availableAgents } = useAgentManifest(config, accessToken)
    const { utgs: availableUtgs } = useUtgManifest(config, accessToken)

    const formRef = useRef(null)
    const componentNickname = useSignal('')
    const componentMerchant = useSignal('')
    const componentLocation = useSignal('')
    const componentFileId = useSignal('')
    const componentLane = useSignal('')

    const tasksSortColumnSignal = useSignal('')
    const devicesSortColumnSignal = useSignal('')
    const SortDirectionType = {
        ASCENDING: 1,
        DESCENDING: 2
    }
    const tasksSortDirectionSignal = useSignal(SortDirectionType.DESCENDING)
    const devicesSortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    const tasksSortedData = useComputed(() => {

        // just return tasks data if no sorting
        if (!tasks.value.length) {
            return [...tasks.value]
        }

        let _sortedData = [...tasks.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, tasksSortColumnSignal.value, null)
            let _b = _.get(b, tasksSortColumnSignal.value, null)

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

            if (tasksSortDirectionSignal.value == SortDirectionType.DESCENDING) {
                if (_a < _b) return -1
                if (_a > _b) return 1
                return 0
            }
            else {
                if (_a > _b) return -1
                if (_a < _b) return 1
                return 0
            }
        })
    })
    const devicesSortedData = useComputed(() => {

        // just return devices data if no sorting
        if (!devices.value.length) {
            return [...devices.value]
        }

        let _sortedData = [...devices.value]
        return _sortedData.sort((a, b) => {
            let _a = _.get(a, devicesSortColumnSignal.value, null)
            let _b = _.get(b, devicesSortColumnSignal.value, null)

            // Convert nulls to empty strings for sorting purposes
            _a = _a ? _a : ''
            _b = _b ? _b : ''

            // convert values to lowercase
            if (_a && _a.length) {
                _a = _a.toLowerCase()
            }
            if (_b && _b.length) {
                _b = _b.toLowerCase()
            }

            if (devicesSortDirectionSignal.value == SortDirectionType.DESCENDING) {
                if (_a < _b) return -1
                if (_a > _b) return 1
                return 0
            }
            else {
                if (_a > _b) return -1
                if (_a < _b) return 1
                return 0
            }
        })
    })
    const handleTasksHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == tasksSortColumnSignal.value) {
            tasksSortDirectionSignal.value = tasksSortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        tasksSortColumnSignal.value = datafield
    }
    const handleDevicesHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == devicesSortColumnSignal.value) {
            devicesSortDirectionSignal.value = devicesSortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        devicesSortColumnSignal.value = datafield
    }

    useSignalEffect(() => {
        componentNickname.value = utg.value.ComponentNickname
        componentMerchant.value = utg.value.ComponentMerchant
        componentLocation.value = utg.value.ComponentLocation
        componentFileId.value = utg.value.ComponentFileId
        componentLane.value = utg.value.ComponentLane
    })
    const handleInput = (event, signalVariable) => {
        signalVariable.value = event.currentTarget.value
    }

    const accordionData = [
        { name: 'UTG Data', data: utg },
        { name: 'Component', data: component },
        { name: 'Utg Connection Data', data: utgConnectionData },
        { name: 'Component Config Data', data: componentConfigData },
        { name: 'Machine Status', data: machineStatusData },
        { name: 'Devices', data: devices },
        { name: 'Tasks', data: tasks },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handleSave = (event) => {
        event.preventDefault()
        event.stopPropagation()

        // deep clone the component data
        let clonedData = structuredClone(component.value)
        clonedData.Nickname = componentNickname.value
        clonedData.Merchant = componentMerchant.value
        clonedData.Location = componentLocation.value
        clonedData.FileId = componentFileId.value
        clonedData.Lane = componentLane.value
        console.log(JSON.stringify(clonedData))

        // update and display a notification
        UpdateComponent(beId, clonedData, config, accessToken)
            .then(result => {
                addNotification('The data was successfully updated.', NotificationTypes.SUCCESS, true)
                // navigate(0)
            })
            .catch(error => {
                console.log(error.toString())
                addNotification('Unable to update the data.', NotificationTypes.ERROR, false)
                return false
            })
    }

    const showRemoveTaskModal = useSignal(false)
    const selectedTaskToDeleteSignal = useSignal([])
    const handleCancelRemoveTask = () => showRemoveTaskModal.value = false
    const handleSubmitModalRemoveTask = () => {
        showRemoveTaskModal.value = false

        let promises = []
        for (let i = 0; i < selectedTaskToDeleteSignal.value.length; i++) {
            try {
                promises.push(DeleteTask(selectedTaskToDeleteSignal.value[i].BusinessEntityId, selectedTaskToDeleteSignal.value[i].MachinePermanentId, selectedTaskToDeleteSignal.value[i].TaskId, config, accessToken))
            } catch (error) {
                addNotification(`Unable to remove all selected Tasks`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            }
        }
        Promise.all(promises)
            .then(async (results) => {
                addNotification('The selected Tasks were successfully removed.', NotificationTypes.SUCCESS, false)
                taskListModified.value = true
                await reFetchTaskList(beId, componentId, config, accessToken)
            })
            .catch(error => {
                addNotification(`Unable to remove all selected Tasks`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            })
    }

    const handleRemoveTask = (item) => {
        if (item) {
            selectedTaskToDeleteSignal.value = [item]
            showRemoveTaskModal.value = true
        }
    }

    const handleCopyClipboard = item => {
        if (item) {
            // place the data on the users clipboard if the browser supports it
            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(JSON.stringify(item, null, 2));
                addNotification(`The data for ${item.ComponentNickname} has been copied to your clipboard.`, NotificationTypes.SUCCESS, false, 'Copy Succeeded')
            }
            else {
                addNotification('This browser does not support copying data to the clipboard.', NotificationTypes.INFO, false, 'Copy Failed')
            }
        }
    }

    if (utgLoading.value || componentLoading.value || componentConfigLoading.value || machineStatusLoading.value || tasksLoading.value || devicesLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    function DeviceTableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <DeviceTableRow item={item} key={`${item.DeviceConfigId}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="9" className="text-center">This UTG does not have any devices.</td></tr></tbody>
            )
        }
    }

    function DeviceTableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.ComponentNickname}</td>
                <td>{item.DeviceStatusMachineName}</td>
                <td>{item.DeviceStatusFirmwareVersion}</td>
                <td>...</td>
                <td>{item.DeviceStatusDeviceSerialNumber}</td>
                <td>{item.DeviceConfigDeviceType}</td>
                <td>{item.DeviceConfigApiTerminalId}</td>
                <td>{item.DeviceStatusPciVersion}</td>
            </tr>
        )
    }

    function TaskTableRows({ items }) {
        if (items && items.length) {
            const rows = items.map(item => {
                return <TaskTableRow item={item} key={`${item.TaskId}`} />
            })

            return (
                <tbody>{rows}</tbody>
            )
        }
        else if (items) {
            return (
                <tbody><tr><td colSpan="6" className="text-center">This UTG does not have any tasks.</td></tr></tbody>
            )
        }
    }

    function TaskTableRow({ item }) {
        return (
            <tr>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions"><Icon icon="gear" width="16" height="16"></Icon></Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleCopyClipboard(item)}>Copy Row as JSON</Dropdown.Item>
                            {canEditSignal.value && <Dropdown.Item onClick={() => handleRemoveTask(item)}>Delete Task</Dropdown.Item>}
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.TaskTypeName}</td>
                <td>{item.TaskStatusName}</td>
                <td><TimestampWithUTCTooltip date={item.TaskLastUpdatedAt}></TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip date={item.TaskRunAfter}></TimestampWithUTCTooltip></td>
                <td>{item.TaskBulkLabel}</td>
            </tr>
        )
    }

    function TableColumnHeader({ title, sortable, datafield, sortColumn, sortDirection, handleHeaderTableClick }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumn.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirection.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    return (
        <>
            <ConfirmationTaskModal show={showRemoveTaskModal.value} handleCancel={() => handleCancelRemoveTask()} handleConfirm={() => { handleSubmitModalRemoveTask(); }} selectedTasks={selectedTaskToDeleteSignal} />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                    UTG List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>Manage {utg.value.ComponentNickname}</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={`Manage UTG ${utg.value.ComponentNickname}`} canEditSignal={canEditSignal} />
                <UtgTools componentConfigData={componentConfigData.value}></UtgTools>
            </Stack>
            <UtgNav beId={beId} componentId={componentId} activeKey="1"></UtgNav>

            <Form ref={formRef} validated onSubmit={(event) => handleSave(event)}>

                <Form.Group as={Row} className="mb-3" controlId="ComponentNickname">
                    <Form.Label column sm="2" className="text-end">Component Nickname/IP:</Form.Label>
                    <Form.Control.Feedback>Required</Form.Control.Feedback>
                    <Col sm="10">
                        <Stack>
                            <Form.Control plaintext={!canEditSignal.value} defaultValue={componentNickname.value} onChange={(event) => handleInput(event, componentNickname)} required={canEditSignal.value} type="text" />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentMerchant">
                    <Form.Label column sm="2" className="text-end">Merchant Description:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control plaintext={!canEditSignal.value} defaultValue={componentMerchant.value} onChange={(event) => handleInput(event, componentMerchant)} required={canEditSignal.value} type="text" />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentLocation">
                    <Form.Label column sm="2" className="text-end">Location:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control plaintext={!canEditSignal.value} defaultValue={utg.value.ComponentLocation} onChange={(event) => handleInput(event, componentLocation)} required={canEditSignal.value} type="text" />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentLane">
                    <Form.Label column sm="2" className="text-end">Lane:</Form.Label>
                    <Col sm="10"><Form.Control plaintext={!canEditSignal.value} defaultValue={utg.value.ComponentLane} onChange={(event) => handleInput(event, componentLane)} type="text" /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ComponentFileId">
                    <Form.Label column sm="2" className="text-end">File ID / MID:</Form.Label>
                    <Col sm="10">
                        <Stack>
                            <Form.Control plaintext={!canEditSignal.value} defaultValue={utg.value.ComponentFileId} onChange={(event) => handleInput(event, componentFileId)} required={canEditSignal.value} type="text" />
                            <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </Stack>
                    </Col>
                </Form.Group>

                {canEditSignal.value && <Form.Group as={Row} className="mb-3" controlId="formUpdateButton">
                    <Form.Label column sm="2" className="text-end"></Form.Label>
                    <Col sm="10">
                        <Stack direction="horizontal" gap={3}>
                            <Button type="submit">Update</Button>
                            <Button variant="secondary" onClick={() => navigate(0)}>Cancel</Button>
                        </Stack>
                    </Col>
                </Form.Group>}

                <hr></hr>

                <Form.Group as={Row} className="mb-3" controlId="MachineStatus">
                    <Form.Label column sm="2" className="text-end">Machine Status:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utg.value.MachineStatus} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusEntityLastKnownCommunication">
                    <Form.Label column sm="2" className="text-end">Last Known Communication:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={deserializeJsonDotNetDate(utg.value.MachineStatusEntityLastKnownCommunication).toLocaleString()} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusEntityNextPredictedCommunication">
                    <Form.Label column sm="2" className="text-end">Next Predicted Communication:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={deserializeJsonDotNetDate(utg.value.MachineStatusEntityNextPredictedCommunication).toLocaleString()} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusStandardTimeZone">
                    <Form.Label column sm="2" className="text-end">Time Zone:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utg.value.MachineStatusEntityStandardTimeZone} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="AgentVersion">
                    <Form.Label column sm="2" className="text-end">Agent Version:</Form.Label>
                        <VersionTooltip
                            version={utg.value.AgentVersion}
                            availableVersions={availableAgents.value}
                            context="Agent"
                        />
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="UtgVersion">
                    <Form.Label column sm="2" className="text-end">UTG Version:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utg.value.UtgVersion} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="MachineStatusWindowsVersion">
                    <Form.Label column sm="2" className="text-end">Windows Version:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utg.value.MachineStatusEntityWindowsVersion} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="ConfigStatus">
                    <Form.Label column sm="2" className="text-end">Configuration Status:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utg.value.ConfigStatus} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="formParentAccount">
                    <Form.Label column sm="2" className="text-end">Parent Account:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue="" /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="formConnectionIn4mListenerAddress">
                    <Form.Label column sm="2" className="text-end">Shift4 Command Center Listener IP:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utgConnectionData.value.In4mListenerAddress} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="formConnectionIn4mListenerPort">
                    <Form.Label column sm="2" className="text-end">Shift4 Command Center Listener Port:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utgConnectionData.value.In4mListenerPort} /></Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-3" controlId="formConnectionUtgExeFilePath">
                    <Form.Label column sm="2" className="text-end">UTG Path:</Form.Label>
                    <Col sm="10"><Form.Control plaintext readOnly defaultValue={utgConnectionData.value.UtgExeFilePath} /></Col>
                </Form.Group>
            </Form >
            <hr></hr>

            <h2 className="display-4 fs-4 px-3 my-3 mt-5">Devices</h2>
            <Table responsive striped size="sm">
                <thead>
                    <tr className="fw-bold">
                        <TableColumnHeader title={'Action'} sortable={false} datafield={''} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Service Name'} sortable={true} datafield={'ComponentNickname'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Name'} sortable={true} datafield={'DeviceStatusMachineName'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Firmware'} sortable={true} datafield={'DeviceStatusFirmwareVersion'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Keys'} sortable={false} datafield={''} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Serial'} sortable={true} datafield={'DeviceStatusDeviceSerialNumber'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Device Type'} sortable={true} datafield={'DeviceConfigDeviceType'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Terminal ID'} sortable={true} datafield={'DeviceConfigApiTerminalId'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'PCI Version'} sortable={true} datafield={'DeviceStatusPciVersion'} sortColumn={devicesSortColumnSignal} sortDirection={devicesSortDirectionSignal} handleHeaderTableClick={handleDevicesHeaderTableClick}></TableColumnHeader>
                    </tr>
                </thead>
                <DeviceTableRows items={devicesSortedData.value} />
            </Table>

            <h2 className="display-4 fs-4 px-3 my-3 mt-5">Scheduled Tasks</h2>
            <Table responsive striped hover size="sm">
                <thead>
                    <tr className="fw-bold">

                        <TableColumnHeader title={'Action'} sortable={false} datafield={''} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Type'} sortable={true} datafield={'TaskTypeName'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Status'} sortable={true} datafield={'TaskStatusName'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Last updated'} sortable={true} datafield={'TaskLastUpdatedAt'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Scheduled time'} sortable={true} datafield={"TaskRunAfter"} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                        <TableColumnHeader title={'Task Label'} sortable={true} datafield={'TaskBulkLabel'} sortColumn={tasksSortColumnSignal} sortDirection={tasksSortDirectionSignal} handleHeaderTableClick={handleTasksHeaderTableClick}></TableColumnHeader>
                    </tr>
                </thead>
                <TaskTableRows items={tasksSortedData.value} />
            </Table>

            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}